import { useState, useMemo } from "react";
import type { Database } from "@/types/database.types";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search, Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

type TaskWithDetailsRow = Database["public"]["Views"]["tasks_with_details"]["Row"];

interface TaskSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tasks: TaskWithDetailsRow[];
  onTaskClick: (task: TaskWithDetailsRow) => void;
}

export function TaskSearchDialog({
  open,
  onOpenChange,
  tasks,
  onTaskClick,
}: TaskSearchDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredTasks = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase().trim();
    return tasks.filter((task) => {
      const titleMatch = task.title?.toLowerCase().includes(query) || false;
      const descriptionMatch = task.description?.toLowerCase().includes(query) || false;
      return titleMatch || descriptionMatch;
    });
  }, [tasks, searchQuery]);

  const handleTaskClick = (task: TaskWithDetailsRow) => {
    onTaskClick(task);
    onOpenChange(false);
    setSearchQuery("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Search Tasks</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 flex-1 min-h-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tasks by title or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              autoFocus
            />
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            {!searchQuery.trim() ? (
              <div className="text-center text-muted-foreground py-8">
                Start typing to search tasks...
              </div>
            ) : filteredTasks.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No tasks found matching &quot;{searchQuery}&quot;
              </div>
            ) : (
              <div className="space-y-2">
                {filteredTasks.map((task) => (
                  <button
                    key={task.id}
                    onClick={() => handleTaskClick(task)}
                    className={cn(
                      "w-full text-left p-4 rounded-lg border hover:bg-accent transition-colors",
                      "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm mb-1 truncate">{task.title}</h3>
                        {task.description && (
                          <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                            {task.description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>{task.project_name}</span>
                          {task.due_date && (
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="h-3 w-3" />
                              {format(new Date(task.due_date), "MMM d, yyyy")}
                            </span>
                          )}
                          {task.status && (
                            <span
                              className={cn(
                                "px-2 py-0.5 rounded text-xs",
                                task.status === "done" &&
                                  "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
                                task.status === "in_progress" &&
                                  "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
                                task.status === "todo" &&
                                  "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
                              )}
                            >
                              {task.status.replace("_", " ")}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {searchQuery.trim() && filteredTasks.length > 0 && (
            <div className="text-xs text-muted-foreground text-center pt-2 border-t">
              Found {filteredTasks.length} task{filteredTasks.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}